import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useHighchartsTheme } from "./useHighchartsTheme";

// Load Highcharts modules
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/export-data")(Highcharts);
require("highcharts/modules/boost")(Highcharts);

interface Props {
  series: Partial<Highcharts.SeriesOptionsType>[];
  chartTitle?: string;
  chartSubtitle?: string;
  yAxisOptions?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
  xAxisOptions?: Highcharts.XAxisOptions;
  xAxisTitle?: string;
  enableLegend?: boolean;
  tooltipOptions?: Highcharts.TooltipOptions;
  sineWaves?: 0 | 1 | 3;
  setSineWaves: Dispatch<SetStateAction<0 | 1 | 3>>;
  reflow?: number;
}

const exportMenuItems = ["downloadPNG", "downloadCSV"];

export default function ScatterPlot(props: Props) {
  const { t } = useTranslation();
  const theme = useHighchartsTheme();
  Highcharts.setOptions(theme);

  let {
    series,
    chartTitle,
    chartSubtitle,
    yAxisOptions,
    xAxisOptions,
    xAxisTitle,
    enableLegend,
    tooltipOptions,
    sineWaves,
    setSineWaves,
  } = props;

  const handleSineWaveChange = useCallback(() => {
    if (sineWaves === 0) {
      setSineWaves(1);
    } else if (sineWaves === 1) {
      setSineWaves(3);
    } else {
      setSineWaves(0);
    }
  }, [setSineWaves, sineWaves]);

  const ref = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    ref.current?.chart.reflow();
  }, [props.reflow]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "scatter",
        marginRight: Array.isArray(yAxisOptions) && yAxisOptions.length > 1 ? 70 : null,
      },
      time: {
        useUTC: false,
      },
      title: {
        text: chartTitle,
      },
      subtitle: {
        text: chartSubtitle,
      },
      legend: {
        enabled: enableLegend,
      },
      tooltip: tooltipOptions,
      xAxis: {
        type: "number",
        title: {
          text: xAxisTitle,
        },
        ...xAxisOptions,
      },
      yAxis: yAxisOptions,
      plotOptions: {
        series: {
          marker: {
            symbol: "circle",
            radius: 2,
          },
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: exportMenuItems,
          },
          menuButton: {
            text: t("common.sineWaves"),
            onclick: handleSineWaveChange,
          },
        },
      },
      boost: {
        enabled: true,
        seriesThreshold: 1,
      },
      series: series,
    };
  }, [
    series,
    chartSubtitle,
    chartTitle,
    enableLegend,
    tooltipOptions,
    xAxisTitle,
    yAxisOptions,
    xAxisOptions,
    handleSineWaveChange,
    t,
  ]) as Highcharts.Options;

  return (
    <HighchartsReact
      ref={ref}
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={{ style: { height: "100%", width: "100%" } }}
    />
  );
}
